/**
 * AITemplateGeneratorPage — wizard → Review → OpenAI generation → results.
 *
 * OpenAI is now the DESIGNER: clicking "Generate Templates" sends the wizard
 * brief to OpenAI (via the secure backend proxy; an offline mock by default)
 * and the deterministic compiler turns the returned Design JSON into native
 * editor documents. The old Blueprint/DesignSpec/Creative engines no longer
 * decide the design. Everything downstream (results, "Open in Editor") is
 * unchanged, and the editor is never modified.
 *
 * Wizard state, taxonomy cascades, and the knowledge panel are untouched.
 */
import React, { useState } from 'react';
import AdminLayout from '../../pages/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { BrainCircuit, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import SectionField from '../components/SectionField';
import DocumentInfoPanel from '../components/DocumentInfoPanel';
import WizardProgress from '../components/wizard/WizardProgress';
import ReviewSummary from '../components/wizard/ReviewSummary';
import GenerationScreen from '../components/wizard/GenerationScreen';
import ResultsScreen from '../components/wizard/ResultsScreen';
import { useGeneratorState } from '../hooks/useGeneratorState';
import { DOCUMENT_TYPES } from '../constants/documentTypes';
import { SECTIONS } from '../constants/generatorSections';
import { WIZARD_STEPS } from '../constants/wizardSteps';
import {
  REGIONS, LANGUAGES, QUALITY_LEVELS, VARIATION_LEVELS, IMAGE_PREFERENCES,
} from '../constants/brandOptions';
import { getDocumentKnowledgeByName } from '../documentKnowledge';
import { taxonomyEngine } from '../taxonomy';

// Sentinel first option for unset taxonomy fields — SectionField consumes
// plain name strings, so ids ⇄ names are mapped at this boundary only.
const UNSET = 'Select…';
const BATCH_SIZES = ['1', '3', '5', '10'];

/**
 * A brief value as one readable line for the review card.
 *
 * Brand colours and contact details are small objects; rendered raw they
 * become "[object Object]" on the very screen meant to confirm the brief.
 */
const displayValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const parts = Object.entries(value)
    .filter(([, v]) => typeof v === 'string' && v.trim())
    .map(([k, v]) => `${k}: ${v}`);
  return parts.join(' · ');
};

/** Option lists for the wizard's non-taxonomy selects. */
const BRAND_OPTIONS = {
  region: REGIONS,
  language: LANGUAGES,
  quality: QUALITY_LEVELS,
  variation: VARIATION_LEVELS,
  images: IMAGE_PREFERENCES,
};

const MODES = { WIZARD: 'wizard', GENERATING: 'generating', RESULTS: 'results' };

const sectionById = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));
const REVIEW_INDEX = WIZARD_STEPS.length - 1;

const AITemplateGeneratorPage = () => {
  const {
    selections, select, documentType, setDocumentType,
    brand, setBrandField, settings, setSettingField,
  } = useGeneratorState();
  const [mode, setMode] = useState(MODES.WIZARD);
  const [stepIndex, setStepIndex] = useState(0);
  const [count, setCount] = useState(1);
  const [runToken, setRunToken] = useState(0);
  const [results, setResults] = useState([]);

  const step = WIZARD_STEPS[stepIndex];

  const taxonomyProps = (fieldId) => {
    const options = taxonomyEngine.getOptions(fieldId, selections);
    const selectedItem = taxonomyEngine.getItem(fieldId, selections[fieldId]);
    return {
      options: [UNSET, ...options.map((item) => item.name)],
      value: selectedItem ? selectedItem.name : UNSET,
      onChange: (name) => {
        const item = options.find((opt) => opt.name === name);
        select(fieldId, item ? item.id : null);
      },
    };
  };

  const valueFor = (sectionId) => {
    if (sectionId === 'documentType') return documentType || '';
    if (sectionId === 'batchSize') return `${count}`;
    if (sectionById[sectionId]?.taxonomy) {
      return taxonomyEngine.getItem(sectionId, selections[sectionId])?.name || '';
    }
    if (sectionId in brand) return displayValue(brand[sectionId]);
    if (sectionId in settings) return displayValue(settings[sectionId]);
    return '';
  };

  /** "Generate Templates" → OpenAI generation (mock offline). */
  const startGeneration = () => { setRunToken((t) => t + 1); setMode(MODES.GENERATING); };
  const retryGeneration = () => setRunToken((t) => t + 1);

  const finishGeneration = (cards) => { setResults(cards); setMode(MODES.RESULTS); };
  const backToReview = () => { setMode(MODES.WIZARD); setStepIndex(REVIEW_INDEX); };

  const fieldProps = (section) => {
    if (section.id === 'documentType') return { value: documentType, onChange: setDocumentType, options: DOCUMENT_TYPES };
    if (section.id === 'batchSize') return { value: `${count}`, onChange: (v) => setCount(Number(v)), options: BATCH_SIZES };
    if (section.taxonomy) return taxonomyProps(section.id);
    if (section.id in brand) {
      return { value: brand[section.id], onChange: (v) => setBrandField(section.id, v), options: BRAND_OPTIONS[section.id] || [] };
    }
    if (section.id in settings) {
      return { value: settings[section.id], onChange: (v) => setSettingField(section.id, v), options: BRAND_OPTIONS[section.id] || [] };
    }
    return {};
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BrainCircuit className="w-6 h-6 text-purple-600" /> AI Template Generator
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            OpenAI designs unique editable templates from your brief; they open in the iVisora editor.
          </p>
        </div>

        {mode === MODES.GENERATING && (
          <GenerationScreen
            selections={selections}
            brand={brand}
            settings={settings}
            count={count}
            runToken={runToken}
            onComplete={finishGeneration}
            onCancel={backToReview}
            onRetry={retryGeneration}
          />
        )}

        {mode === MODES.RESULTS && (
          <ResultsScreen
            key={results.map((r) => r.id).join(',')}
            results={results}
            // Carried through so a published template lands in the library with
            // the brief's own type/industry/category as searchable tags.
            brief={{ ...selections, documentType }}
            onGenerateMore={backToReview}
          />
        )}

        {mode === MODES.WIZARD && (
          <>
            <WizardProgress currentIndex={stepIndex} />

            <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">{step.title}</h2>

              {step.id === 'review' ? (
                <ReviewSummary valueFor={valueFor} />
              ) : (
                <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {step.fields.map((fieldId) => {
                    const section = sectionById[fieldId];
                    return <SectionField key={section.id} section={section} {...fieldProps(section)} />;
                  })}
                </div>
              )}

              {step.id === 'document' && (
                <div className="max-w-2xl mx-auto mt-6">
                  <DocumentInfoPanel definition={getDocumentKnowledgeByName(documentType)} />
                </div>
              )}

              <div className="flex items-center justify-between max-w-2xl mx-auto mt-8 pt-6 border-t border-gray-100">
                {stepIndex > 0 ? (
                  <Button variant="outline" onClick={() => setStepIndex((i) => i - 1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                ) : (
                  <span />
                )}
                {stepIndex < REVIEW_INDEX ? (
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setStepIndex((i) => i + 1)}>
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={startGeneration}>
                    <Sparkles className="w-4 h-4 mr-2" /> Generate Templates
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AITemplateGeneratorPage;
